// MFA enrolment UI used during signup. The user has already created an
// email/password account; this step enrols a second factor before we let
// them into the app. CLAUDE.md says 2FA is mandatory at signup.
//
// Flow:
//   - User picks TOTP (recommended) or SMS.
//   - TOTP: we generate a secret, render a QR code, user scans with their
//     authenticator app, types the first 6-digit code, and we enrol.
//   - SMS: user types a phone number, we send a code via Firebase's
//     invisible reCAPTCHA flow, user types the code, and we enrol.
//
// The component takes the signed-in `user` and an `onEnrolled` callback that
// the parent uses to advance the signup flow (write user doc, redirect).

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  TotpMultiFactorGenerator,
  TotpSecret,
  type User,
} from 'firebase/auth';
import { QRCodeSVG } from 'qrcode.react';
import { auth } from '../../firebase';
import { authErrorMessage } from '../../lib/auth-errors';

type Factor = 'totp' | 'sms';
type Stage = 'pick-factor' | 'totp-show-qr' | 'sms-collect-phone' | 'sms-collect-code';

export default function MfaEnrollmentStep({
  user,
  onEnrolled,
}: {
  user: User;
  onEnrolled: (factor: Factor) => void;
}) {
  const [factor, setFactor] = useState<Factor | null>(null);
  const [stage, setStage] = useState<Stage>('pick-factor');

  function back() {
    setStage('pick-factor');
    setFactor(null);
  }

  if (stage === 'pick-factor') {
    return (
      <div>
        <h2>Set up two-factor auth</h2>
        <p style={{ color: 'var(--muted)' }}>
          Required to finish creating your account. Pick a method.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem' }}>
          <button
            type="button"
            onClick={() => {
              setFactor('totp');
              setStage('totp-show-qr');
            }}
          >
            Authenticator app (recommended)
          </button>
          <button
            type="button"
            onClick={() => {
              setFactor('sms');
              setStage('sms-collect-phone');
            }}
          >
            SMS to my phone
          </button>
        </div>
      </div>
    );
  }

  if (factor === 'totp' && stage === 'totp-show-qr') {
    return <TotpEnrolment user={user} onEnrolled={() => onEnrolled('totp')} onBack={back} />;
  }

  if (factor === 'sms') {
    return <SmsEnrolment user={user} onEnrolled={() => onEnrolled('sms')} onBack={back} />;
  }

  return null;
}

// ---------- TOTP sub-component ----------

function TotpEnrolment({
  user,
  onEnrolled,
  onBack,
}: {
  user: User;
  onEnrolled: () => void;
  onBack: () => void;
}) {
  const [secret, setSecret] = useState<TotpSecret | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const session = await multiFactor(user).getSession();
        const s = await TotpMultiFactorGenerator.generateSecret(session);
        if (!cancelled) setSecret(s);
      } catch (err) {
        // Log the raw error so we can see Firebase's code/message in dev.
        // The user-facing copy stays in authErrorMessage; this is for us.
        console.error('[mfa] TOTP secret generation failed', err);
        if (!cancelled) setError(authErrorMessage(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const qrUrl = useMemo(
    () => (secret ? secret.generateQrCodeUrl(user.email ?? user.uid, 'Rouser') : null),
    [secret, user.email, user.uid],
  );

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!secret || busy) return;
    setBusy(true);
    setError(null);
    try {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code.trim());
      await multiFactor(user).enroll(assertion, 'Authenticator app');
      onEnrolled();
    } catch (err) {
      console.error('[mfa] TOTP enrol failed', err);
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h2>Scan with your authenticator</h2>
      <p style={{ color: 'var(--muted)' }}>
        1Password, Google Authenticator, Authy, etc. After scanning, enter the 6-digit code below.
      </p>
      {error && <p style={{ color: 'salmon' }}>{error}</p>}

      {qrUrl ? (
        <div
          style={{
            background: '#fff',
            padding: '1rem',
            display: 'inline-block',
            borderRadius: 8,
            margin: '1rem 0',
          }}
        >
          <QRCodeSVG value={qrUrl} size={180} />
        </div>
      ) : (
        <p style={{ color: 'var(--muted)' }}>Generating secret…</p>
      )}

      {secret && (
        <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
          Can&apos;t scan?{' '}
          <code style={{ background: 'rgba(255,255,255,0.06)', padding: '0.1rem 0.3rem' }}>
            {secret.secretKey}
          </code>
        </p>
      )}

      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <input
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          maxLength={6}
          placeholder="6-digit code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          required
        />
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="button" onClick={onBack} disabled={busy}>
            Back
          </button>
          <button type="submit" disabled={busy || !secret || code.length !== 6}>
            {busy ? 'Verifying…' : 'Verify and enrol'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------- SMS sub-component ----------

function SmsEnrolment({
  user,
  onEnrolled,
  onBack,
}: {
  user: User;
  onEnrolled: () => void;
  onBack: () => void;
}) {
  const [stage, setStage] = useState<'collect-phone' | 'collect-code'>('collect-phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const recaptchaContainerRef = useRef<HTMLDivElement | null>(null);
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);

  useEffect(() => {
    if (!recaptchaContainerRef.current) return;
    const verifier = new RecaptchaVerifier(auth, recaptchaContainerRef.current, {
      size: 'invisible',
    });
    recaptchaVerifierRef.current = verifier;
    return () => {
      verifier.clear();
      recaptchaVerifierRef.current = null;
    };
  }, []);

  async function sendCode(e: React.FormEvent) {
    e.preventDefault();
    if (busy || !recaptchaVerifierRef.current) return;
    setBusy(true);
    setError(null);
    try {
      const session = await multiFactor(user).getSession();
      const provider = new PhoneAuthProvider(auth);
      const id = await provider.verifyPhoneNumber(
        { phoneNumber: phone.trim(), session },
        recaptchaVerifierRef.current,
      );
      setVerificationId(id);
      setStage('collect-code');
    } catch (err) {
      console.error('[mfa] SMS send code failed', err);
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    if (busy || !verificationId) return;
    setBusy(true);
    setError(null);
    try {
      const cred = PhoneAuthProvider.credential(verificationId, code.trim());
      const assertion = PhoneMultiFactorGenerator.assertion(cred);
      await multiFactor(user).enroll(assertion, `Phone (${phone.trim()})`);
      onEnrolled();
    } catch (err) {
      console.error('[mfa] SMS enrol failed', err);
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h2>SMS verification</h2>
      {error && <p style={{ color: 'salmon' }}>{error}</p>}

      {stage === 'collect-phone' && (
        <form
          onSubmit={sendCode}
          style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
        >
          <p style={{ color: 'var(--muted)' }}>
            Enter your phone number in international format (e.g. +447700900123).
          </p>
          <input
            type="tel"
            placeholder="+44…"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
          />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="button" onClick={onBack} disabled={busy}>
              Back
            </button>
            <button type="submit" disabled={busy || !phone.trim()}>
              {busy ? 'Sending…' : 'Send code'}
            </button>
          </div>
        </form>
      )}

      {stage === 'collect-code' && (
        <form
          onSubmit={submitCode}
          style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
        >
          <p style={{ color: 'var(--muted)' }}>We sent a 6-digit code to {phone.trim()}.</p>
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            placeholder="6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
          />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="button" onClick={() => setStage('collect-phone')} disabled={busy}>
              Use a different number
            </button>
            <button type="submit" disabled={busy || code.length !== 6}>
              {busy ? 'Verifying…' : 'Verify and enrol'}
            </button>
          </div>
        </form>
      )}

      <div ref={recaptchaContainerRef} />
    </div>
  );
}
